/**
 * <s-scroll-arrows> — previous/next buttons for a horizontal scroll-snap track
 *
 * Markup contract (inside the element):
 *   [data-scroll-track] — the horizontally scrolling element
 *   [data-scroll-prev]  — button; scrolls back one track width
 *   [data-scroll-next]  — button; scrolls forward one track width
 *
 * Buttons are disabled at each end of the track and re-sync on scroll and resize.
 * Scrolling respects writing direction (RTL) and prefers-reduced-motion.
 */
class SScrollArrows extends HTMLElement {
  #ac;
  #track = null;
  #prev = null;
  #next = null;
  #frame = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-scroll-track]');
    this.#prev = this.querySelector('[data-scroll-prev]');
    this.#next = this.querySelector('[data-scroll-next]');
    if (!this.#track || !this.#prev || !this.#next) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#prev.addEventListener('click', () => this.#step(-1), opts);
    this.#next.addEventListener('click', () => this.#step(1), opts);
    this.#track.addEventListener('scroll', () => this.#queueSync(), { ...opts, passive: true });
    window.addEventListener('resize', () => this.#queueSync(), opts);

    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #rtl() {
    return getComputedStyle(this.#track).direction === 'rtl';
  }

  #step(direction) {
    const sign = this.#rtl ? -1 : 1;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollBy({ left: direction * sign * this.#track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
  }

  #queueSync() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  }

  #sync() {
    const track = this.#track;
    // scrollLeft is negative in RTL; compare magnitudes so both directions work.
    const position = Math.abs(track.scrollLeft);
    const max = track.scrollWidth - track.clientWidth;
    this.#prev.disabled = position <= 2;
    this.#next.disabled = position >= max - 2;
  }
}

if (!customElements.get('s-scroll-arrows')) {
  customElements.define('s-scroll-arrows', SScrollArrows);
}
